'use client';

import { useCallback, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const EMAIL_PROVIDERS = [
  'gmail.com',
  'outlook.com',
  'hotmail.com',
  'uol.com.br',
  'icloud.com',
] as const;

interface EmailDomainAutocompleteProps {
  email: string;
  onSelect: (completedEmail: string) => void;
  visible?: boolean;
}

export function EmailDomainAutocomplete({
  email,
  onSelect,
  visible = true,
}: EmailDomainAutocompleteProps) {
  const [open, setOpen] = useState(true);

  const atIndex = email.indexOf('@');
  const hasAt = atIndex !== -1;
  const localPart = hasAt ? email.slice(0, atIndex) : '';
  const domainFragment = hasAt ? email.slice(atIndex + 1).toLowerCase() : '';

  const suggestions = useMemo(() => {
    if (!hasAt || !localPart) return [];
    if (
      EMAIL_PROVIDERS.includes(
        domainFragment as (typeof EMAIL_PROVIDERS)[number],
      )
    )
      return [];
    return EMAIL_PROVIDERS.filter((provider) =>
      provider.startsWith(domainFragment),
    );
  }, [hasAt, localPart, domainFragment]);

  const hasSuggestions = visible && suggestions.length > 0;
  const showDropdown = hasSuggestions && open;

  const handleSelect = useCallback(
    (provider: string) => {
      onSelect(`${localPart}@${provider}`);
    },
    [localPart, onSelect],
  );

  if (!hasSuggestions) return null;

  return (
    <>
      {/* Chevron toggle — positioned inside the input via parent's relative */}
      <button
        type="button"
        onMouseDown={(e) => {
          e.preventDefault();
          setOpen((prev) => !prev);
        }}
        className="absolute top-1/2 right-4 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors z-10"
        aria-label={open ? 'Fechar sugestões' : 'Abrir sugestões'}
      >
        <ChevronDown
          className={`size-5 transition-transform duration-200 ${
            showDropdown ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {showDropdown && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 top-full mt-1 overflow-hidden rounded-2xl border border-input bg-background shadow-lg z-20"
            role="listbox"
          >
            {suggestions.map((provider) => (
              <div key={provider} role="option" aria-selected={false}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(provider);
                  }}
                  className="w-full text-left px-4 py-3 text-base text-muted-foreground hover:bg-accent hover:text-primary transition-colors"
                >
                  <span className="text-primary/70">{localPart}@</span>
                  <span className="font-medium text-primary">{provider}</span>
                </button>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
